/**
 * TwaMachineSection - Wiederverwendbarer TWA-Report Block für eine Maschine
 * 
 * Zeigt KPIs, Charts und Schichtdaten-Tabelle für eine spezifische Maschine (P25 oder P50).
 * Wird von TwaReport.tsx verwendet um zwei separate Bereiche zu rendern.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Loader2,
  AlertTriangle,
  TrendingUp,
  Pencil,
  X,
  ChevronRight,
  ChevronDown,
  Clock,
  Check,
} from 'lucide-react';
import { HorizontalBarChart } from './BarChart';
import { Tooltip } from './Tooltip';
import { ExportButtonsPlaceholder } from './ExportButtonsPlaceholder';
import { formatBerlinDateDE, formatBerlinDateISO } from '../utils/berlinDate';

// Types
interface TwaEntry {
  id: string;
  machineCode: string;
  shiftDate: string;
  shiftTypeCodeSnap: string;
  shiftRuleCodeSnap: string;
  shiftRuleNameSnap: string | null;
  shiftResolutionStatus: 'MATCHED' | 'FALLBACK' | 'BACKFILL';
  timestampQuality?: 'OK' | 'SKEWED' | 'MISSING' | 'PARSE_ERROR';
  eventAtSource?: 'FILE' | 'AGENT';
  rawFileTimestamp?: string;
  firstSeenAt?: string;
  agentTimestamp: string;
  serverStoredAt?: string;
  laufzeitH: number | null;
  stoerzeitH: number | null;
  standzeitH: number | null;
  gesamttakte: number | null;
  gesamtgewichtKg: number | null;
  gewichtProTaktKg: number | null;
  takteProStunde: number | null;
  wasserProKgL: number | null;
  effectiveShiftDate?: string;
  effectiveShiftTypeCode?: string;
  isOverridden?: boolean;
  overrideInfo?: {
    reason: string;
    by: string;
    at: string;
  };
}

interface ChartData {
  id: string;
  name: string;
  value: number;
  takte?: number;
}

interface DayData {
  date: string;
  kg: number;
  takte: number;
  laufzeit: number;
  stoerzeit: number;
  count: number;
}

interface Kennzahlen {
  gesamtgewichtKg: number;
  gesamttakte: number;
  laufzeitH: number;
  stoerzeitH: number;
  standzeitH: number;
  avgTakteProStunde: number | null;
  produktivitaetKgH: number | null;
  nutzungsgradPct: number | null;
  stoeranteilPct: number | null;
  standanteilPct: number | null;
  schichtCount: number;
}

interface TwaShiftRow {
  shiftDate: string;
  shiftType: string;
  kg: number;
  takte: number;
  laufzeitH: number;
  stoerzeitH: number;
  standzeitH: number;
  snapshotsCount: number;
  firstSnapshotAt: string;
  lastSnapshotAt: string;
  isOngoing: boolean;
}

interface TwaShiftSummary {
  totals: {
    totalKg: number;
    totalTakte: number;
    avgKgPerTakt: number | null;
  };
  byShift: TwaShiftRow[];
  asOfBerlin: string;
  hasOngoingShift: boolean;
}

interface TwaDetails {
  kennzahlen: Kennzahlen;
  kundenAggregat: Array<{
    twaKundenNr: number;
    matchStatus: string;
    matchedName: string | null;
    gewichtKg: number;
    takte: number;
  }>;
}

interface Props {
  machineCode: 'TWA_P50' | 'TWA_P25';
  title: string;
  fromDate: string;
  toDate: string;
  vizMode: 'single-day' | 'week' | 'month';
  isPartialRange: boolean;
  shiftTypes: Array<{ code: string; label: string }>;
  onCorrectionSaved?: () => void;
}

const SHIFT_TYPE_LABELS: Record<string, string> = {
  EARLY: 'Frühschicht',
  LATE: 'Spätschicht',
  SATURDAY: 'Samstag',
  UNKNOWN: 'Unbekannt',
};

function getShiftTypeLabel(code: string | undefined): string {
  if (!code) return '—';
  return SHIFT_TYPE_LABELS[code] || code;
}

function getWeekNumber(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}

/**
 * Deterministische Sortierung für Wasser-Daten:
 * 1) "Gesamt" immer ganz oben
 * 2) Kammern nach value desc
 * 3) Tie-breaker: Kammernummer asc
 */
function sortWaterData<T extends { name: string; value: number }>(data: T[]): T[] {
  const getChamberNumber = (name: string): number | null => {
    const match = name.toLowerCase().match(/kammer\s*(\d+)/);
    return match ? parseInt(match[1], 10) : null;
  };
  
  const isGesamt = (name: string): boolean => name.toLowerCase().includes('gesamt');
  
  return [...data].sort((a, b) => {
    const aGesamt = isGesamt(a.name);
    const bGesamt = isGesamt(b.name);
    
    // Gesamt immer oben
    if (aGesamt && !bGesamt) return -1;
    if (!aGesamt && bGesamt) return 1;
    
    // Beide Gesamt oder beide nicht-Gesamt: nach value desc
    if (a.value !== b.value) return b.value - a.value;
    
    // Tie-breaker: Kammernummer asc
    const aNum = getChamberNumber(a.name);
    const bNum = getChamberNumber(b.name);
    if (aNum !== null && bNum !== null) return aNum - bNum;
    
    // Fallback: alphabetisch
    return a.name.localeCompare(b.name, 'de');
  });
}

export function TwaMachineSection({
  machineCode,
  title,
  fromDate,
  toDate,
  vizMode,
  isPartialRange,
  shiftTypes,
  onCorrectionSaved,
}: Props) {
  const [entries, setEntries] = useState<TwaEntry[]>([]);
  const [byDay, setByDay] = useState<DayData[]>([]);
  const [byProgram, setByProgram] = useState<ChartData[]>([]);
  const [waterData, setWaterData] = useState<ChartData[]>([]);
  const [details, setDetails] = useState<TwaDetails | null>(null);
  const [shiftSummary, setShiftSummary] = useState<TwaShiftSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Expand/Collapse State für Drilldown
  const [expandedShiftKey, setExpandedShiftKey] = useState<string | null>(null);

  const [totalKg, setTotalKg] = useState(0);
  const [totalTakte, setTotalTakte] = useState(0);

  // "vorläufig" nur wenn mindestens eine Schicht noch läuft
  const hasOngoingShift = shiftSummary?.byShift?.some(row => row.isOngoing) ?? false;

  // Korrektur-Dialog State
  const [correctionDialog, setCorrectionDialog] = useState<{
    open: boolean;
    entry: TwaEntry | null;
    saving: boolean;
  }>({ open: false, entry: null, saving: false });
  const [correctionForm, setCorrectionForm] = useState({
    shiftDate: '',
    shiftType: 'EARLY',
    reason: '',
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const mc = `&machineCode=${machineCode}`;
      const [entriesRes, byDayRes, byProgramRes, waterRes, detailsRes, shiftSummaryRes] = await Promise.all([
        fetch(`/api/reports/twa?from=${fromDate}&to=${toDate}${mc}`, { credentials: 'include' }),
        fetch(`/api/reports/twa/by-day?from=${fromDate}&to=${toDate}${mc}`, { credentials: 'include' }),
        fetch(`/api/reports/twa/by-program?from=${fromDate}&to=${toDate}${mc}`, { credentials: 'include' }),
        fetch(`/api/reports/twa/water?from=${fromDate}&to=${toDate}${mc}`, { credentials: 'include' }),
        fetch(`/api/reports/twa/details?from=${fromDate}&to=${toDate}${mc}`, { credentials: 'include' }),
        fetch(`/api/reports/twa/shift-summary?from=${fromDate}&to=${toDate}${mc}`, { credentials: 'include' }),
      ]);

      if (entriesRes.ok) {
        const data = await entriesRes.json();
        setEntries(data?.entries ?? []);
        setTotalKg(data?.summary?.totalKg ?? 0);
        setTotalTakte(data?.summary?.totalTakte ?? 0);
      }
      if (shiftSummaryRes.ok) {
        const summaryData = await shiftSummaryRes.json();
        setShiftSummary(summaryData);
      }
      if (byDayRes.ok) {
        const dayData = await byDayRes.json();
        setByDay(Array.isArray(dayData) ? dayData : []);
      }
      if (byProgramRes.ok) {
        const programData = await byProgramRes.json();
        setByProgram(Array.isArray(programData) ? programData : []);
      }
      if (waterRes.ok) {
        const water = await waterRes.json();
        setWaterData(Array.isArray(water) ? water : []);
      }
      if (detailsRes.ok) {
        const detailsData = await detailsRes.json();
        setDetails(detailsData);
      }
    } catch {
      setError('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, machineCode]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleExport = () => {
    const headers = ['Datum', 'Schicht', 'Gewicht (kg)', 'Takte', 'Laufzeit (h)', 'Störzeit (h)', 'kg/Takt', 'Takte/h'];
    const rows = entries.map((e) => [
      formatBerlinDateDE(e.shiftDate),
      getShiftTypeLabel(e.effectiveShiftTypeCode ?? e.shiftTypeCodeSnap),
      e.gesamtgewichtKg?.toLocaleString('de-DE') ?? '—',
      e.gesamttakte?.toString() ?? '—',
      e.laufzeitH?.toFixed(1) ?? '—',
      e.stoerzeitH?.toFixed(1) ?? '—',
      e.gewichtProTaktKg?.toFixed(1) ?? '—',
      e.takteProStunde?.toFixed(1) ?? '—',
    ]);

    const csv = [headers, ...rows].map((row) => row.join(';')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `twa-${machineCode.toLowerCase()}-${fromDate}-${toDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const openCorrectionDialog = (entry: TwaEntry) => {
    const rawDate = entry.rawFileTimestamp
      ? formatBerlinDateISO(entry.rawFileTimestamp)
      : formatBerlinDateISO(entry.shiftDate);
    setCorrectionForm({
      shiftDate: rawDate,
      shiftType: 'EARLY',
      reason: '',
    });
    setCorrectionDialog({ open: true, entry, saving: false });
  };

  const saveCorrection = async () => {
    if (!correctionDialog.entry) return;
    if (correctionForm.reason.trim().length < 5) {
      alert('Begründung muss mindestens 5 Zeichen haben.');
      return;
    }

    setCorrectionDialog((prev) => ({ ...prev, saving: true }));

    try {
      const res = await fetch(`/api/admin/twa-events/${correctionDialog.entry.id}/override`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          overrideShiftDate: correctionForm.shiftDate,
          overrideShiftTypeCode: correctionForm.shiftType,
          overrideReason: correctionForm.reason.trim(),
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Fehler beim Speichern');
      }

      setCorrectionDialog({ open: false, entry: null, saving: false });
      loadData();
      onCorrectionSaved?.();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Fehler beim Speichern');
      setCorrectionDialog((prev) => ({ ...prev, saving: false }));
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">{title}</h2>
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">{title}</h2>
        <div className="text-center py-12">
          <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-2" />
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  // Berechne unknownCount für Banner
  const unknownCount = shiftSummary?.byShift.filter(s => s.shiftType === 'UNKNOWN').length ?? 0;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
<ExportButtonsPlaceholder csvOnly />
      </div>

      {/* Handlungsbedarf Banner */}
      {unknownCount > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-800">
              Handlungsbedarf: {unknownCount} Schicht{unknownCount > 1 ? 'en' : ''} ohne Zuordnung
            </p>
            <p className="text-xs text-amber-700 mt-0.5">
              Die TWA-Uhrzeit konnte nicht geprüft werden. Bitte Schicht manuell zuweisen, damit die Auswertung korrekt ist.
            </p>
          </div>
          <button
            onClick={() => {
              const tableElement = document.getElementById(`twa-shift-table-${machineCode}`);
              tableElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
            className="px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-100 hover:bg-amber-200 rounded-lg transition-colors flex-shrink-0"
          >
            Schichten prüfen
          </button>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card">
          <div className="flex items-center gap-2">
            <Tooltip content="Gesamtgewicht der gewaschenen Wäsche im gewählten Zeitraum.">
              <p className="text-sm text-gray-500 border-b border-dotted border-gray-400 cursor-help">Gesamt</p>
            </Tooltip>
            {hasOngoingShift && <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">vorläufig</span>}
          </div>
          <p className="text-2xl font-bold text-gray-900">{totalKg.toLocaleString('de-DE')} kg</p>
        </div>
        <div className="card">
          <div className="flex items-center gap-2">
            <Tooltip content="Anzahl der Wasch-Takte im gewählten Zeitraum.">
              <p className="text-sm text-gray-500 border-b border-dotted border-gray-400 cursor-help">Takte</p>
            </Tooltip>
            {hasOngoingShift && <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">vorläufig</span>}
          </div>
          <p className="text-2xl font-bold text-gray-900">{totalTakte.toLocaleString('de-DE')}</p>
        </div>
        <div className="card">
          <div className="flex items-center gap-2">
            <Tooltip content="Durchschnittliches Gewicht pro Wasch-Takt.">
              <p className="text-sm text-gray-500 border-b border-dotted border-gray-400 cursor-help">Ø kg / Takt</p>
            </Tooltip>
            {hasOngoingShift && <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">vorläufig</span>}
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {totalTakte > 0 ? (totalKg / totalTakte).toFixed(1) : '–'}
          </p>
        </div>
        <div className="card">
          <Tooltip content="Anzahl der Schichten im gewählten Zeitraum (aggregiert nach Datum + Schichttyp).">
            <p className="text-sm text-gray-500 border-b border-dotted border-gray-400 cursor-help">Schichten</p>
          </Tooltip>
          <p className="text-2xl font-bold text-gray-900">{shiftSummary?.byShift.length ?? 0}</p>
        </div>
      </div>

      {/* Tagesverlauf (Week Mode) */}
      {vizMode === 'week' && byDay.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            Tagesverlauf
          </h3>
          <div className="flex items-end justify-between gap-2 h-48 pt-4">
            {(() => {
              const maxKg = Math.max(...byDay.map(d => d.kg), 1);
              return byDay.map((day) => {
                const pct = (day.kg / maxKg) * 100;
                const weekday = new Date(day.date + 'T12:00:00').toLocaleDateString('de-DE', { timeZone: 'Europe/Berlin', weekday: 'short' });
                return (
                  <div key={day.date} className="flex-1 flex flex-col items-center gap-1">
                    <span className="text-xs font-medium text-gray-600">
                      {day.kg > 0 ? day.kg.toLocaleString('de-DE') : ''}
                    </span>
                    <div className="w-full flex justify-center">
                      <div
                        className="w-8 bg-blue-500 rounded-t transition-all"
                        style={{ height: `${Math.max(pct, 2)}%`, minHeight: day.kg > 0 ? '8px' : '2px' }}
                      />
                    </div>
                    <span className="text-xs text-gray-500">{weekday}</span>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      )}

      {/* Monats-Modus: Stacked Bar Chart nach Schicht */}
      {vizMode === 'month' && shiftSummary && shiftSummary.byShift.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Tagesverlauf nach Schicht
            </h3>
            <div className="flex items-center gap-3 text-xs text-gray-500">
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-blue-500" />Frühschicht</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-green-500" />Spätschicht</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-purple-500" />Samstag</span>
            </div>
          </div>
          {(() => {
            const SHIFT_COLORS: Record<string, string> = { EARLY: '#3b82f6', LATE: '#22c55e', SATURDAY: '#a855f7' };
            const dayMap = new Map<string, Record<string, number>>();
            for (const row of shiftSummary.byShift) {
              if (!dayMap.has(row.shiftDate)) dayMap.set(row.shiftDate, {});
              const entry = dayMap.get(row.shiftDate)!;
              entry[row.shiftType] = (entry[row.shiftType] ?? 0) + row.kg;
            }
            const days = Array.from(dayMap.entries()).sort((a, b) => a[0].localeCompare(b[0]));
            const maxKg = Math.max(...days.map(([, shifts]) => Object.values(shifts).reduce((s, v) => s + v, 0)), 1);

            return (
              <div className="flex items-end gap-1 h-48 pt-4">
                {days.map(([date, shifts]) => {
                  const totalKg = Object.values(shifts).reduce((s, v) => s + v, 0);
                  const weekday = new Date(date + 'T12:00:00').toLocaleDateString('de-DE', { timeZone: 'Europe/Berlin', weekday: 'short' });
                  const dayNum = new Date(date + 'T12:00:00').toLocaleDateString('de-DE', { timeZone: 'Europe/Berlin', day: '2-digit' });
                  const segments = (['EARLY', 'LATE', 'SATURDAY'] as const).filter(t => (shifts[t] ?? 0) > 0);
                  return (
                    <div key={date} className="flex-1 flex flex-col items-center gap-1 min-w-0" title={`${weekday} ${dayNum}: ${totalKg.toLocaleString('de-DE')} kg`}>
                      <span className="text-[10px] font-medium text-gray-500 truncate w-full text-center">
                        {totalKg > 0 ? totalKg.toLocaleString('de-DE') : ''}
                      </span>
                      <div className="w-full flex justify-center">
                        <div className="w-6 flex flex-col-reverse" style={{ height: `${Math.max((totalKg / maxKg) * 100, 2)}%`, minHeight: totalKg > 0 ? '8px' : '2px' }}>
                          {segments.map(type => {
                            const segKg = shifts[type] ?? 0;
                            const segPct = totalKg > 0 ? (segKg / totalKg) * 100 : 0;
                            return (
                              <div
                                key={type}
                                className={type === segments[0] ? 'rounded-t' : ''}
                                style={{ height: `${segPct}%`, backgroundColor: SHIFT_COLORS[type], minHeight: '2px' }}
                              />
                            );
                          })}
                        </div>
                      </div>
                      <span className="text-[10px] text-gray-400">{weekday}</span>
                      <span className="text-[10px] text-gray-300">{dayNum}</span>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      )}

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Programme */}
        {byProgram.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Programme (kg)</h3>
            <HorizontalBarChart 
              data={byProgram.slice(0, 8).map(p => ({ ...p, fullName: p.name }))} 
              title=""
              unit="kg"
              height={200}
              maxItems={8}
            />
          </div>
        )}

        {/* Wasser */}
        {waterData.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Wasser (L)</h3>
            <HorizontalBarChart 
              data={sortWaterData(waterData).map(w => ({ ...w, fullName: w.name }))} 
              title=""
              unit="L"
              height={200}
              maxItems={10}
              preserveOrder
            />
          </div>
        )}

        {/* Zeitaufteilung */}
        {details?.kennzahlen && (
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Zeitaufteilung</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Laufzeit</span>
                <span className="font-medium">{details.kennzahlen.laufzeitH.toFixed(1)} h</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Störzeit</span>
                <span className="font-medium">{details.kennzahlen.stoerzeitH.toFixed(1)} h</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Standzeit</span>
                <span className="font-medium">{details.kennzahlen.standzeitH.toFixed(1)} h</span>
              </div>
            </div>
          </div>
        )}

        {/* Nutzungsgrad */}
        {details?.kennzahlen && (
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Nutzungsgrad</h3>
            <div className="space-y-3">
              {details.kennzahlen.nutzungsgradPct != null && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Nutzungsgrad</span>
                  <span className="font-medium text-green-600">{details.kennzahlen.nutzungsgradPct.toFixed(1)}%</span>
                </div>
              )}
              {details.kennzahlen.stoeranteilPct != null && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Störanteil</span>
                  <span className="font-medium text-red-600">{details.kennzahlen.stoeranteilPct.toFixed(1)}%</span>
                </div>
              )}
              {details.kennzahlen.standanteilPct != null && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Standanteil</span>
                  <span className="font-medium text-gray-600">{details.kennzahlen.standanteilPct.toFixed(1)}%</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Schichtdaten Tabelle (aggregiert nach Schicht) */}
      <div id={`twa-shift-table-${machineCode}`} className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
          <h3 className="text-sm font-medium text-gray-700">Schichtdaten ({shiftSummary?.byShift.length ?? 0})</h3>
          {unknownCount > 0 && (
            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
              {unknownCount} ohne Zuordnung
            </span>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  <Tooltip content="Datum der Schicht (aggregiert nach Datum + Schichttyp).">
                    <span className="border-b border-dotted border-gray-400 cursor-help">Schichtdatum</span>
                  </Tooltip>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  <Tooltip content="Schichttyp (Früh, Spät, Samstag).">
                    <span className="border-b border-dotted border-gray-400 cursor-help">Schicht</span>
                  </Tooltip>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  <Tooltip content="Status der Schicht.">
                    <span className="border-b border-dotted border-gray-400 cursor-help">Status</span>
                  </Tooltip>
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  <Tooltip content="Gesamtgewicht der in dieser Schicht gewaschenen Wäsche.">
                    <span className="border-b border-dotted border-gray-400 cursor-help">Gewicht</span>
                  </Tooltip>
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  <Tooltip content="Anzahl der Waschvorgänge (Takte) in dieser Schicht.">
                    <span className="border-b border-dotted border-gray-400 cursor-help">Takte</span>
                  </Tooltip>
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  <Tooltip content="Produktive Betriebszeit der Maschine (in Stunden).">
                    <span className="border-b border-dotted border-gray-400 cursor-help">Laufzeit</span>
                  </Tooltip>
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  <Tooltip content="Anzahl der CSV-Snapshots in dieser Schicht.">
                    <span className="border-b border-dotted border-gray-400 cursor-help">Snapshots</span>
                  </Tooltip>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  <Tooltip content="Zeitpunkt des letzten CSV-Imports für diese Schicht.">
                    <span className="border-b border-dotted border-gray-400 cursor-help">Letzter Import</span>
                  </Tooltip>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(!shiftSummary || shiftSummary.byShift.length === 0) ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                    Keine Daten im gewählten Zeitraum
                  </td>
                </tr>
              ) : (
                shiftSummary.byShift.map((row) => {
                  const shiftKey = `${row.shiftDate}|${row.shiftType}`;
                  const isExpanded = expandedShiftKey === shiftKey;
                  const snapshotsForShift = entries.filter(e => {
                    const entryShiftDate = formatBerlinDateISO(e.effectiveShiftDate ?? e.shiftDate);
                    const entryShiftType = e.effectiveShiftTypeCode ?? e.shiftTypeCodeSnap;
                    return entryShiftDate === row.shiftDate && entryShiftType === row.shiftType;
                  });

                  return (
                    <React.Fragment key={shiftKey}>
                      <tr 
                        className={`cursor-pointer ${
                          row.shiftType === 'UNKNOWN' 
                            ? 'bg-amber-50 hover:bg-amber-100 border-l-4 border-l-amber-400' 
                            : 'hover:bg-gray-50'
                        }`}
                        onClick={() => setExpandedShiftKey(isExpanded ? null : shiftKey)}
                      >
                        <td className="px-4 py-3 text-sm text-gray-900">
                          <div className="flex items-center gap-2">
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4 text-gray-400" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-gray-400" />
                            )}
                            {formatBerlinDateDE(row.shiftDate + 'T12:00:00Z')}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {row.shiftType === 'UNKNOWN' ? (
                            <Tooltip content="Die Schicht konnte nicht automatisch erkannt werden.">
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs bg-amber-100 text-amber-700 rounded font-medium">
                                <AlertTriangle className="w-3 h-3" />
                                Nicht erkannt
                              </span>
                            </Tooltip>
                          ) : (
                            <span className="text-gray-900">{getShiftTypeLabel(row.shiftType)}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {row.shiftType === 'UNKNOWN' ? (
                            <Tooltip content="Die TWA-Uhrzeit konnte nicht geprüft werden. Bitte klicken Sie auf die Zeile und weisen Sie die Schicht manuell zu.">
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs bg-red-100 text-red-700 rounded font-medium">
                                <AlertTriangle className="w-3 h-3" />
                                Schicht zuweisen
                              </span>
                            </Tooltip>
                          ) : row.isOngoing ? (
                            <Tooltip content="Diese Schicht läuft gerade. Die Werte können sich noch ändern.">
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
                                <Clock className="w-3 h-3" />
                                Aktiv
                              </span>
                            </Tooltip>
                          ) : (
                            <Tooltip content="Für diese Schicht wurden keine weiteren Daten empfangen.">
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs bg-green-100 text-green-700 rounded">
                                <Check className="w-3 h-3" />
                                Abgeschlossen
                              </span>
                            </Tooltip>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">
                          {row.kg.toLocaleString('de-DE')} kg
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-right">
                          {row.takte.toLocaleString('de-DE')}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-right">
                          {row.laufzeitH.toFixed(1)} h
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 text-right">
                          {row.snapshotsCount}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {new Date(row.lastSnapshotAt).toLocaleString('de-DE', {
                            day: '2-digit',
                            month: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </td>
                      </tr>
                      {isExpanded && snapshotsForShift.length > 0 && (
                        <tr>
                          <td colSpan={8} className="bg-slate-50 px-4 py-2">
                            <div className="ml-6 border-l-2 border-slate-200 pl-4">
                              <p className="text-xs font-medium text-slate-500 mb-2">
                                CSV-Snapshots ({snapshotsForShift.length})
                              </p>
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="text-xs text-slate-500">
                                    <th className="text-left py-1 pr-4 font-medium">Import-Zeit</th>
                                    <th className="text-left py-1 pr-4 font-medium">Anlagenzeit (Datei)</th>
                                    <th className="text-left py-1 pr-4 font-medium">Zeitstatus</th>
                                    <th className="text-right py-1 pr-4 font-medium">Gewicht</th>
                                    <th className="text-right py-1 pr-6 font-medium">Takte</th>
                                    <th className="text-left py-1 pr-4 font-medium">Zuordnung</th>
                                    <th className="text-left py-1 pr-4 font-medium">Schichtkorrektur</th>
                                    <th className="text-center py-1 font-medium">Aktion</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {snapshotsForShift.map((snapshot) => (
                                    <tr key={snapshot.id} className="border-t border-slate-100">
                                      <td className="py-2 text-slate-700">
                                        {new Date(snapshot.serverStoredAt ?? snapshot.agentTimestamp).toLocaleString('de-DE', {
                                          day: '2-digit',
                                          month: '2-digit',
                                          hour: '2-digit',
                                          minute: '2-digit',
                                        })}
                                      </td>
                                      <td className="py-2 text-slate-600">
                                        {snapshot.rawFileTimestamp
                                          ? new Date(snapshot.rawFileTimestamp).toLocaleString('de-DE', {
                                              day: '2-digit',
                                              month: '2-digit',
                                              hour: '2-digit',
                                              minute: '2-digit',
                                            })
                                          : '—'}
                                      </td>
                                      <td className="py-2 pr-4">
                                        {!snapshot.firstSeenAt ? (
                                          <Tooltip content="Altbestand: Beim Import lag keine Gerätezeit vor, eine Umrechnung war nicht möglich. Zuordnung bei Bedarf von Hand prüfen.">
                                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs bg-red-100 text-red-700 rounded">
                                              <AlertTriangle className="w-3 h-3" />
                                              Nicht prüfbar
                                            </span>
                                          </Tooltip>
                                        ) : snapshot.timestampQuality === 'OK' ? (
                                          <Tooltip content="Der Zeitstempel der Anlagensteuerung wurde geprüft — die Abweichung ist gering, die Originalzeit wird verwendet.">
                                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs bg-green-100 text-green-700 rounded">
                                              <Check className="w-3 h-3" />
                                              Geprüft
                                            </span>
                                          </Tooltip>
                                        ) : snapshot.timestampQuality === 'SKEWED' && snapshot.eventAtSource === 'FILE' ? (
                                          <Tooltip content="Die Uhr der Anlagensteuerung geht vor (aktuell rund eine Stunde). Der Zeitstempel wurde beim Import auf die tatsächliche Zeit umgerechnet — seit August 2026 anhand der direkt an der Anlage gemessenen Uhr.">
                                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
                                              <Check className="w-3 h-3" />
                                              Umgerechnet
                                            </span>
                                          </Tooltip>
                                        ) : snapshot.timestampQuality === 'SKEWED' && snapshot.eventAtSource === 'AGENT' ? (
                                          <Tooltip content="Für dieses Gerät lag beim Import noch kein verlässlicher Uhrenversatz vor. Ersatzweise gilt die Import-Zeit.">
                                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs bg-amber-100 text-amber-700 rounded">
                                              <Clock className="w-3 h-3" />
                                              Ersetzt
                                            </span>
                                          </Tooltip>
                                        ) : snapshot.timestampQuality === 'MISSING' ? (
                                          <Tooltip content="Legacy-Import: Import-Zeit fehlt. Prüfung/Korrektur nicht zuverlässig möglich – bitte manuell prüfen.">
                                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs bg-red-100 text-red-700 rounded">
                                              <AlertTriangle className="w-3 h-3" />
                                              Nicht prüfbar
                                            </span>
                                          </Tooltip>
                                        ) : (
                                          <Tooltip content="Unbekannter Zeitstatus – bitte prüfen.">
                                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs bg-red-100 text-red-700 rounded">
                                              <AlertTriangle className="w-3 h-3" />
                                              Nicht prüfbar
                                            </span>
                                          </Tooltip>
                                        )}
                                      </td>
                                      <td className="py-2 pr-4 text-slate-700 text-right">
                                        {snapshot.gesamtgewichtKg?.toLocaleString('de-DE') ?? '—'} kg
                                      </td>
                                      <td className="py-2 pr-6 text-slate-700 text-right">
                                        {snapshot.gesamttakte?.toLocaleString('de-DE') ?? '—'}
                                      </td>
                                      <td className="py-2 pr-4">
                                        {snapshot.isOverridden ? (
                                          <Tooltip content="Die Schichtzuordnung wurde manuell korrigiert.">
                                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">
                                              <Pencil className="w-3 h-3" />
                                              Manuell
                                            </span>
                                          </Tooltip>
                                        ) : snapshot.shiftResolutionStatus === 'MATCHED' ? (
                                          <Tooltip content="Das System hat die Schicht automatisch erkannt.">
                                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs bg-green-100 text-green-700 rounded">
                                              <Check className="w-3 h-3" />
                                              Automatisch
                                            </span>
                                          </Tooltip>
                                        ) : snapshot.shiftResolutionStatus === 'FALLBACK' ? (
                                          <Tooltip content="Die Schicht konnte nicht automatisch erkannt werden. Bitte prüfen und ggf. manuell zuweisen.">
                                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs bg-amber-100 text-amber-700 rounded">
                                              <AlertTriangle className="w-3 h-3" />
                                              Prüfung nötig
                                            </span>
                                          </Tooltip>
                                        ) : (
                                          <span className="text-xs text-slate-400">—</span>
                                        )}
                                      </td>
                                      <td className="py-2 pr-4">
                                        {snapshot.isOverridden ? (
                                          <Tooltip content={`Grund: ${snapshot.overrideInfo?.reason ?? 'Unbekannt'}`}>
                                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">
                                              <Check className="w-3 h-3" />
                                              Manuell
                                            </span>
                                          </Tooltip>
                                        ) : (
                                          <span className="text-xs text-slate-400">—</span>
                                        )}
                                      </td>
                                      <td className="py-2 text-center">
                                        <Tooltip content="Schichtzuordnung korrigieren">
                                          <button
                                            onClick={(e) => { e.stopPropagation(); openCorrectionDialog(snapshot); }}
                                            className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                                            aria-label="Korrigieren"
                                          >
                                            <Pencil className="w-4 h-4" />
                                          </button>
                                        </Tooltip>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Korrektur-Dialog */}
      {correctionDialog.open && correctionDialog.entry && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-semibold">Schichtzuordnung korrigieren</h3>
              <button
                onClick={() => setCorrectionDialog({ open: false, entry: null, saving: false })}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">CSV-Zeit (Original)</label>
                <p className="text-sm text-gray-600">
                  {correctionDialog.entry.rawFileTimestamp
                    ? new Date(correctionDialog.entry.rawFileTimestamp).toLocaleString('de-DE')
                    : '—'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Korrigiertes Schichtdatum</label>
                <input
                  type="date"
                  value={correctionForm.shiftDate}
                  onChange={(e) => setCorrectionForm((f) => ({ ...f, shiftDate: e.target.value }))}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Schichttyp</label>
                <select
                  value={correctionForm.shiftType}
                  onChange={(e) => setCorrectionForm((f) => ({ ...f, shiftType: e.target.value }))}
                  className="input w-full"
                >
                  {shiftTypes.map((t) => (
                    <option key={t.code} value={t.code}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Begründung <span className="text-red-500">*</span>
                  </label>
                  <span className={`text-xs font-medium ${correctionForm.reason.trim().length >= 5 ? 'text-green-600' : 'text-gray-400'}`}>
                    {correctionForm.reason.trim().length}/5
                  </span>
                </div>
                <textarea
                  value={correctionForm.reason}
                  onChange={(e) => setCorrectionForm((f) => ({ ...f, reason: e.target.value }))}
                  className={`input w-full ${
                    correctionForm.reason.trim().length > 0 && correctionForm.reason.trim().length < 5 
                      ? 'border-amber-400' 
                      : ''
                  }`}
                  rows={3}
                  placeholder="Warum wird die Zuordnung korrigiert?"
                />
                {correctionForm.reason.trim().length < 5 && (
                  <p className="text-xs text-amber-600 mt-1">
                    Mindestens 5 Zeichen erforderlich
                  </p>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50">
              <button
                onClick={() => setCorrectionDialog({ open: false, entry: null, saving: false })}
                className="btn btn-secondary"
                disabled={correctionDialog.saving}
              >
                Abbrechen
              </button>
              <button
                onClick={saveCorrection}
                className="btn btn-primary"
                disabled={correctionDialog.saving || correctionForm.reason.trim().length < 5}
              >
                {correctionDialog.saving ? 'Speichern...' : 'Speichern'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
